import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@shared/components/ui/command';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@shared/components/ui/form';
import { cn } from '@shared/lib/utils';
import Fuse, { FuseOptionKey } from 'fuse.js';
import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useInView } from 'react-intersection-observer';
import { Icons } from '../content/icons';
import Loader from '../loader';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import InputLabel, { InputLabelProps } from './input-label';
import { FormProps } from './types';

export interface SelectSearchOption {
  value: string;
  label: string;
  endAdornment?: React.ReactNode;
}

interface SelectSearchFormFieldProps extends FormProps, InputLabelProps {
  options: SelectSearchOption[];
  disabled?: boolean;
  placeholder?: string;
  pluralName?: string;
  loading?: boolean;
  handleSelect?: (value: string) => void;
  // Infinite scroll props
  onSearchChange?: (search: string) => void;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  selectedOption?: SelectSearchOption;
  renderOption?: (option: SelectSearchOption) => React.ReactNode;
}

interface UseSearchProps<T> {
  items: T[];
  searchQuery: string;
  searchKeys: FuseOptionKey<T>[];
}

const useSearch = <T,>({
  items,
  searchQuery,
  searchKeys,
}: UseSearchProps<T>): { filteredOptions: T[] } => {
  const fuse = useMemo(() => {
    return new Fuse(items, {
      keys: searchKeys,
      threshold: 0.1,
      shouldSort: false,
    });
  }, [items, searchKeys]);

  if (!searchQuery) {
    return { filteredOptions: items };
  }
  return {
    filteredOptions: fuse.search(searchQuery).map((result) => result.item),
  };
};

export default function SelectSearchFormField({
  name,
  includeErrorMessage = false,
  className,
  label,
  options,
  disabled,
  placeholder,
  pluralName,
  tooltip,
  loading,
  optional = false,
  handleSelect,
  // New props
  onSearchChange,
  isLoadingMore,
  hasMore,
  onLoadMore,
  selectedOption,
  renderOption,
}: SelectSearchFormFieldProps) {
  const { control } = useFormContext();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { ref, inView } = useInView();

  const { filteredOptions } = useSearch({
    items: options || [],
    searchQuery: onSearchChange ? '' : searchQuery,
    searchKeys: ['label'],
  });

  if (inView && hasMore && onLoadMore && !isLoadingMore) {
    onLoadMore();
  }

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={cn('grid items-end w-full', className)}>
          {label && (
            <InputLabel
              label={label}
              name={name}
              optional={optional}
              tooltip={tooltip}
            />
          )}
          <FormControl>
            <div className="max-w-full overflow-hidden">
              <Popover
                open={open}
                onOpenChange={(isOpen) => {
                  setOpen(isOpen);
                  setSearchQuery('');
                }}
                modal={true}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled || loading}
                    className={cn(
                      'h-10 w-full justify-between px-3 font-normal',
                      !field.value && 'text-muted-foreground',
                      fieldState.error && 'border-red-500'
                    )}
                  >
                    {loading && !selectedOption ? (
                      <Loader size="xs" />
                    ) : (
                      <span className="truncate">
                        {field.value
                          ? options?.find(
                              (option) => option.value === field.value
                            )?.label ||
                            (selectedOption?.value === field.value
                              ? selectedOption?.label
                              : null) ||
                            placeholder
                          : placeholder || ''}
                      </span>
                    )}
                    <Icons.chevronDown className="ml-2 size-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="p-0"
                  align="start"
                  onWheel={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={
                        pluralName ? `Search ${pluralName}...` : 'Search...'
                      }
                      value={searchQuery}
                      onValueChange={handleSearch}
                    />
                    <CommandEmpty className="pb-2 pt-4">
                      No {pluralName || 'items'} found
                    </CommandEmpty>
                    <CommandGroup>
                      <CommandList>
                        {' '}
                        {
                          // Ensure selected option is visible in the list if it's not in the filtered options
                          // but only if we are not searching (to avoid confusing search results)
                          selectedOption &&
                            !searchQuery &&
                            !filteredOptions.find(
                              (op) => op.value === selectedOption.value
                            ) && (
                              <CommandItem
                                key={selectedOption.value}
                                value={selectedOption.value}
                                onSelect={(value) => {
                                  field.onChange(selectedOption.value);
                                  handleSelect && handleSelect(value);
                                  setOpen(false);
                                }}
                              >
                                <div className="flex w-full items-center">
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4 flex-shrink-0',
                                      field.value === selectedOption.value
                                        ? 'opacity-100'
                                        : 'opacity-0'
                                    )}
                                  />
                                  {renderOption
                                    ? renderOption(selectedOption)
                                    : selectedOption.label}
                                  {selectedOption.endAdornment}
                                </div>
                              </CommandItem>
                            )
                        }
                        {filteredOptions.length > 0 &&
                          filteredOptions.map((option) => (
                            <CommandItem
                              key={option.value}
                              value={option.value}
                              onSelect={(value) => {
                                field.onChange(option.value);
                                handleSelect && handleSelect(value);
                                setOpen(false);
                              }}
                            >
                              <div className="flex w-full items-center">
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4 flex-shrink-0',
                                    field.value === option.value
                                      ? 'opacity-100'
                                      : 'opacity-0'
                                  )}
                                />
                                {renderOption
                                  ? renderOption(option)
                                  : option.label}
                                {option.endAdornment}
                              </div>
                            </CommandItem>
                          ))}
                        {hasMore && (
                          <div ref={ref} className="flex justify-center py-2">
                            {isLoadingMore && <Loader size="xs" />}
                          </div>
                        )}
                      </CommandList>
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </FormControl>
          {includeErrorMessage && <FormMessage />}
        </FormItem>
      )}
    />
  );
}
